import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import defaultContent from './defaultContent.json';

export const LANGUAGES = ['es', 'pt', 'en'];
const LANG_STORAGE_KEY = '8bits-lang';
// Cada cuánto se revisa si el admin publicó cambios (ms)
const UPDATE_CHECK_INTERVAL = 30 * 1000;

const safeStorage = {
  get(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  },
  set(key, value) {
    try { window.localStorage.setItem(key, value); } catch (e) { /* modo privado */ }
  },
};

// Idioma según el dispositivo: portugués → pt, español → es, inglés → en.
// Cualquier otro idioma cae en inglés.
export const detectLanguage = () => {
  const saved = safeStorage.get(LANG_STORAGE_KEY);
  if (LANGUAGES.includes(saved)) return saved;

  const preferred = (typeof navigator !== 'undefined' && (navigator.languages || [navigator.language])) || [];
  for (const tag of preferred) {
    const code = String(tag || '').toLowerCase().slice(0, 2);
    if (LANGUAGES.includes(code)) return code;
  }
  return 'en';
};

// Devuelve el texto en el idioma actual. Si falta la traducción usa español.
export const translate = (value, lang) => {
  if (value === null || value === undefined) return '';
  if (typeof value !== 'object') return value;
  return value[lang] || value.es || value.en || value.pt || '';
};

const fetchJson = async (url) => {
  const res = await fetch(`${url}${url.includes('?') ? '&' : '?'}t=${Date.now()}`, { cache: 'no-store' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

// Borra caches del navegador y service workers antes de recargar
export const hardReload = async () => {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    if (window.caches) {
      const keys = await window.caches.keys();
      await Promise.all(keys.map((k) => window.caches.delete(k)));
    }
  } catch (e) { /* no bloquear la recarga */ }
  window.location.reload();
};

const SiteContext = createContext(null);

export const SiteProvider = ({ children }) => {
  const [lang, setLangState] = useState(detectLanguage);
  const [content, setContent] = useState(defaultContent);
  const [ready, setReady] = useState(false);
  const versionRef = useRef(null);

  const setLang = useCallback((next) => {
    if (!LANGUAGES.includes(next)) return;
    safeStorage.set(LANG_STORAGE_KEY, next);
    setLangState(next);
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang === 'pt' ? 'pt-BR' : lang;
  }, [lang]);

  const loadContent = useCallback(async () => {
    try {
      const data = await fetchJson('/api/content.php');
      if (data && typeof data === 'object' && data.hero) setContent(data);
    } catch (e) {
      // Sin servidor PHP (o error): se queda el contenido incluido en la web
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    // No dejar la pantalla vacía si el servidor tarda
    const fallback = setTimeout(() => !cancelled && setReady(true), 2500);
    loadContent().finally(() => {
      if (!cancelled) setReady(true);
      clearTimeout(fallback);
    });
    return () => { cancelled = true; clearTimeout(fallback); };
  }, [loadContent]);

  // Revisa si hay una versión nueva publicada desde el admin
  useEffect(() => {
    const currentMainScript = () => {
      const s = Array.from(document.scripts).find((el) => /\/static\/js\/main\.[^/]+\.js/.test(el.src));
      return s ? new URL(s.src).pathname : null;
    };

    const check = async () => {
      try {
        const v = await fetchJson('/api/version.php');
        const prev = versionRef.current;
        versionRef.current = v;
        if (prev) {
          if (v.forceReload !== prev.forceReload) return hardReload();
          if (v.contentVersion !== prev.contentVersion) await loadContent();
        }
      } catch (e) { /* sin conexión o sin PHP */ }

      // Si se subió una build nueva de la web, recargar para usarla
      try {
        const mine = currentMainScript();
        if (mine) {
          const manifest = await fetchJson('/asset-manifest.json');
          const latest = manifest && manifest.files && manifest.files['main.js'];
          if (latest && latest !== mine) hardReload();
        }
      } catch (e) { /* en desarrollo no hay manifest */ }
    };

    check();
    const id = setInterval(check, UPDATE_CHECK_INTERVAL);
    const onVisible = () => document.visibilityState === 'visible' && check();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [loadContent]);

  const t = useCallback((value) => translate(value, lang), [lang]);

  return (
    <SiteContext.Provider value={{ lang, setLang, content, t, ready }}>
      {children}
    </SiteContext.Provider>
  );
};

export const useSite = () => useContext(SiteContext);
